import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { supabase } from '@/shared/lib/supabase'
import { useLangParam, SUPPORTED_LANGS, type SupportedLang } from '@/shared/hooks'
import { Loader2 } from 'lucide-react'
import {
  getPendingPrecadrageData,
  createProjectFromPrecadrage,
} from '@/features/precadrage'

/**
 * OAuth/Magic Link callback page.
 *
 * After successful authentication:
 * 1. Fetches user's preferred locale from profile
 * 2. Redirects to /:userLang/dashboard (or /:userLang/p/:shortId if pending precadrage)
 *
 * Uses window.location.href for full page reload after auth.
 */
export function AuthCallbackPage() {
  const { t } = useTranslation()
  const { lang: urlLang } = useLangParam()
  const [error, setError] = useState<string | null>(null)
  const [status, setStatus] = useState<string>('authenticating')

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Supabase will automatically handle the token exchange
        // from the URL hash when we call getSession
        const { data, error: sessionError } = await supabase.auth.getSession()

        if (sessionError) {
          setError(sessionError.message)
          return
        }

        if (data.session) {
          // Fetch user's preferred locale from profile
          const { data: profile } = await supabase
            .from('users')
            .select('locale')
            .eq('id', data.session.user.id)
            .single()

          // Use profile locale, fallback to 'en'
          const rawLang = profile?.locale || 'en'
          const userLang: SupportedLang = SUPPORTED_LANGS.includes(rawLang as SupportedLang)
            ? (rawLang as SupportedLang)
            : 'en'

          // Check if there's pending pre-scoping data to process
          const pendingData = getPendingPrecadrageData()

          if (pendingData) {
            setStatus('creating_project')

            // Create team and project from the pending data
            const result = await createProjectFromPrecadrage()

            if (!result.success) {
              setError(result.error || 'Failed to create project')
              return
            }

            // CADRAGE-V2-001: Redirect to project page (which shows payment prompt)
            if (result.projectShortId) {
              window.location.href = `/${userLang}/p/${result.projectShortId}`
              return
            }
          }

          // User is authenticated, redirect to dashboard
          window.location.href = `/${userLang}/dashboard`
        } else {
          // No session found, redirect to landing
          window.location.href = `/${urlLang}`
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error')
      }
    }

    handleCallback()
  }, [urlLang])

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="text-center">
          <p className="text-destructive">{error}</p>
          <a
            href={`/${urlLang}`}
            className="mt-4 inline-block text-primary hover:underline"
          >
            {t('common.back')}
          </a>
        </div>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-3">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
        <span className="text-foreground-muted">
          {status === 'creating_project'
            ? t('precadrage.callback.creatingProject')
            : t('common.loading')}
        </span>
      </div>
    </div>
  )
}
